import React, { useState, useEffect, useRef, useMemo } from "react";
import Draggable from "react-draggable";
import { Resizable } from "react-resizable";
import "react-resizable/css/styles.css";
import { Cross } from "hamburger-react";
import { ApiUrls } from "../../Utils/API";
import {
  fetchData,
  notifyError,
  patchData,
  isColorLight,
} from "../../Utils/ReusableFunctions";
import { InfoBarModal } from "./InfobarModal";
import { useSettingsInfoBoard } from "../../Context/InfoboardSettingsContext";

export const InfoBottom = ({
  id,
  deleteData,
  selectedComponentId,
  guid,
  isRounded,
  componentName,
}) => {
  const sessionToken = localStorage.getItem("bearerToken");
  const nodeRef = useRef(null);
  const [currentInfoIndex, setCurrentInfoIndex] = useState(0);
  const [infoBarData, setInfoBarData] = useState([]);
  const [componentInfo, setComponentInfo] = useState([]);
  const [listDelay, setListDelay] = useState(null);
  const [infoList, setInfoList] = useState([]);
  const {
    positions,
    setPositionCustomComponent,
    setSizeCustomComponent,
    componentSize,
    zIndex,
  } = useSettingsInfoBoard(id);
  const [isDragging, setIsDragging] = useState(false);
  const [isZindex, setIsZindex] = useState(false);

  useEffect(() => {
    let API;
    if (sessionToken) {
      API = `${ApiUrls.component}(${id})?$expand=media`;
    } else {
      API = `${ApiUrls.getComponentByGuid}(id=${selectedComponentId},Guid=${guid})`;
    }
    fetchData(API, sessionToken)
      .then((data) => {
        const settingsArray = JSON.parse(
          data.settingsJson || data.SettingsJson
        );
        setComponentInfo(settingsArray);
        setInfoBarData(data);

        setListDelay(settingsArray.messageDelay);
        setInfoList(settingsArray.infoList);
      })
      .catch((error) => {
        console.error("Failed to fetch data:", error);
        notifyError("Failed to fetch data:", error);
      });
  }, [id, sessionToken, selectedComponentId, guid]);

  useEffect(() => {
    setCurrentInfoIndex(0);
  }, [componentInfo, infoList]);

  useEffect(() => {
    const interval = setInterval(
      () => {
        setCurrentInfoIndex((prevIndex) => {
          const nextIndex =
            prevIndex ===
            (selectedComponentId
              ? infoList?.length - 1
              : componentInfo.infoList?.length - 1)
              ? 0
              : prevIndex + 1;
          if (nextIndex === 0) {
            if (selectedComponentId) {
              let API;
              API = `${ApiUrls.getComponentByGuid}(id=${selectedComponentId},Guid=${guid})`;
              fetchData(API, sessionToken).then((data) => {
                const settingsArray = JSON.parse(
                  data.settingsJson || data.SettingsJson
                );
                setListDelay(settingsArray.messageDelay);
                setInfoList(settingsArray.infoList);
                console.log("hello");
              });
            }
          }

          return nextIndex;
        });
      },
      selectedComponentId ? listDelay : componentInfo.messageDelay
    );

    return () => clearInterval(interval);
  }, [
    componentInfo,
    guid,
    selectedComponentId,
    sessionToken,
    infoList,
    listDelay,
  ]);

  const patchSettingsToBackend2 = async (updatedSettings, settingsJson, id) => {
    try {
      const existingSettings = JSON.parse(settingsJson);
      const updatedSettingsJson = JSON.stringify({
        ...existingSettings,
        ...updatedSettings,
      });

      await patchData(`${ApiUrls.component}/${id}`, sessionToken, {
        SettingsJson: updatedSettingsJson,
      });
      fetchData(`${ApiUrls.component}/${id}`, sessionToken)
        .then((data) => {
          const settingsArray = JSON.parse(data.SettingsJson);
          setComponentInfo(settingsArray);
          setInfoList(settingsArray.infoList);
          setInfoBarData(data);
          setSizeCustomComponent(id, {
            width: (settingsArray.width * window.innerWidth) / 24,
            height: (settingsArray.height * window.innerHeight) / 24,
          });
          setPositionCustomComponent(id, {
            x: (settingsArray.xPosition * window.innerWidth) / 24,
            y: (settingsArray.yPosition * window.innerHeight) / 24,
          });
        })
        .catch((error) => {
          notifyError("Failed to fetch data:", error);
        });
    } catch (error) {
      notifyError("Error patching settings to the backend:", error);
    }
  };

  const initialSize = useMemo(
    () =>
      componentSize[id] || {
        height: (componentInfo?.height * window.innerHeight) / 24,
        width: (componentInfo?.width * window.innerWidth) / 24,
      },
    [id, componentSize, componentInfo]
  );

  const initialFontSize = {
    textSize: useMemo(
      () =>
        selectedComponentId
          ? componentInfo.textSize
          : (componentInfo.textSize * initialSize.height) / window.innerHeight,
      [componentInfo.textSize, initialSize.height, selectedComponentId]
    ),
  };

  const initialPosition = useMemo(
    () =>
      positions[id] || {
        x: (componentInfo.xPosition * window.innerWidth) / 24,
        y: (componentInfo.yPosition * window.innerHeight) / 24,
      },
    [id, positions, componentInfo]
  );
  const tempPosition = useRef(initialPosition);

  const handleDrag = (e, ui) => {
    const { x, y } = tempPosition.current;
    tempPosition.current = { x: x + ui.deltaX, y: y + ui.deltaY };
    setIsDragging(true);
  };

  useEffect(() => {
    tempPosition.current = initialPosition;
  }, [initialPosition]);

  const handleResize = (event, { size }) => {
    if (!selectedComponentId) {
      const newWidth = Math.round((size.width / window.innerWidth) * 24);
      const newHeight = Math.round((size.height / window.innerHeight) * 24);
      patchSettingsToBackend2(
        {
          width: newWidth,
          height: newHeight,
        },
        infoBarData.SettingsJson,
        id
      );
    }
  };

  const handleDragStop = () => {
    if (isDragging) {
      const newX = Math.round(
        (tempPosition.current.x / window.innerWidth) * 24
      );
      const newY = Math.round(
        (tempPosition.current.y / window.innerHeight) * 24
      );
      patchSettingsToBackend2(
        {
          xPosition: newX,
          yPosition: newY,
        },
        infoBarData.SettingsJson,
        id
      );
      setPositionCustomComponent(id, tempPosition.current);
      setIsDragging(false);
    }
  };

  function formatDate(dateString) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const widthStep = window.innerWidth / 24;
  const heightStep = window.innerHeight / 24;

  useEffect(() => {
    const handleWindowResize = () => {
      fetchData(`${ApiUrls.component}/${id}`, sessionToken).then((data) => {
        const settingsArray = JSON.parse(data.SettingsJson);
        setSizeCustomComponent(id, {
          width: (settingsArray.width * window.innerWidth) / 24,
          height: (settingsArray.height * window.innerHeight) / 24,
        });
        setPositionCustomComponent(id, {
          x: (settingsArray.xPosition * window.innerWidth) / 24,
          y: (settingsArray.yPosition * window.innerHeight) / 24,
        });
      });
    };

    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [
    componentInfo,
    id,
    sessionToken,
    setSizeCustomComponent,
    setPositionCustomComponent,
  ]);

  const result = isColorLight(componentInfo?.color);

  const styles = {
    hamburger: {
      position: "absolute",
      top: initialSize.height < 50 ? null : 10,
      right: 10,
      zIndex: 1,
      color: result ? "black" : "white",
    },
  };


  return (
    <>
      {initialSize.width && initialSize.height ? (
        <Draggable
          onDrag={handleDrag}
          onStop={handleDragStop}
          position={!selectedComponentId ? initialPosition : { x: 0, y: 0 }}
          cancel=".react-resizable-handle"
          nodeRef={nodeRef}
          grid={[widthStep, heightStep]}
          bounds="parent"
          disabled={selectedComponentId != null}
        >
          <Resizable
            width={initialSize.width}
            height={initialSize.height}
            onResize={(event, { size }) =>
              !selectedComponentId && setSizeCustomComponent(id, size)
            }
            onResizeStop={handleResize}
            minConstraints={[widthStep, heightStep]}
            maxConstraints={[widthStep * 24, heightStep * 24]}
            draggableOpts={{ grid: [widthStep, heightStep] }}
            handle={selectedComponentId && <></>}
          >
            <div
              className={`${
                selectedComponentId
                  ? ""
                  : zIndex
                  ? "hoverEffectzIndex"
                  : "hoverEffect"
              } d-flex justify-content-center align-items-center opacity-${
                componentInfo.opacity
              }`}
              onMouseEnter={() => {
                setIsZindex(true);
              }}
              onMouseLeave={() => {
                setIsZindex(false);
              }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                height: selectedComponentId ? "100%" : initialSize.height,
                width: selectedComponentId ? "100%" : initialSize.width,
                backgroundColor: `${componentInfo.color}`,
                borderRadius: isRounded ? "10px" : "",
                zIndex: isZindex ? 100 : zIndex ? zIndex : componentInfo.zIndex,
              }}
              ref={nodeRef}
            >
              {!selectedComponentId && (
                <div
                  style={styles.hamburger}
                  type="button"
                  data-bs-toggle="modal"
                  data-bs-target={`#infobarModal${id}`}
                >
                  <Cross size={25} toggled={false} />
                </div>
              )}

              {selectedComponentId ? (
                infoList && infoList.length > 0 ? (
                  <div
                    className={`${componentInfo.boldText && "fw-bolder"}`}
                    style={{
                      fontSize: `${initialFontSize.textSize}vh`,
                      color: componentInfo.textColor,
                    }}
                  >
                    {infoList[currentInfoIndex] && (
                      <>
                        <div>{infoList[currentInfoIndex].message}</div>
                        <div>{formatDate(infoList[currentInfoIndex].date)}</div>
                      </>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      color: componentInfo.textColor,
                      fontSize: `${initialFontSize.textSize}vh`,
                    }}
                  >
                    No messages available
                  </div>
                )
              ) : componentInfo &&
                componentInfo.infoList &&
                componentInfo.infoList.length > 0 ? (
                <div
                  className={`${componentInfo.boldText && "fw-bolder"}`}
                  style={{
                    fontSize: `${initialFontSize.textSize}vh`,
                    color: componentInfo.textColor,
                  }}
                >
                  {componentInfo.infoList[currentInfoIndex] && (
                    <>
                      <div>
                        {componentInfo.infoList[currentInfoIndex].message}
                      </div>
                      <div>
                        {formatDate(
                          componentInfo.infoList[currentInfoIndex].date
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    color: componentInfo.textColor,
                    fontSize: `${initialFontSize.textSize}vh`,
                  }}
                >
                  No messages available
                </div>
              )}
            </div>
          </Resizable>
        </Draggable>
      ) : (
        <div></div>
      )}
      {!selectedComponentId && (
        <InfoBarModal
          id={id}
          patchSettingsToBackend2={patchSettingsToBackend2}
          infoBarData={infoBarData}
          componentInfo={componentInfo}
          positions={positions}
          deleteData={deleteData}
          componentName={componentName}
        />
      )}
    </>
  );
};
